import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useFetch } from "../lib/hooks";
import { fileUrl } from "../lib/api";
import { Reveal, Overline } from "../components/site/Reveal";
import PageHeader from "../components/site/PageHeader";
import EnquiryForm from "../components/site/EnquiryForm";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { ArrowRight } from "lucide-react";

export default function Menus() {
  const { data: menu, loading } = useFetch("/public/menu");
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(null);
  const [enquiry, setEnquiry] = useState(null);

  useEffect(() => {
    if (!menu.length) return;
    const c = params.get("c");
    const found = menu.find((m) => m.id === c);
    setActive(found || menu[0]);
  }, [menu, params]);

  const shapeCls = { arch: "frame-arch", oval: "rounded-[46%]", portrait: "rounded-3xl", pill: "frame-pill" };

  return (
    <div>
      <PageHeader overline="The Culinary Collection" title={<>Explore the <span className="italic gold-text">culinary collection.</span></>}
        subtitle="A premium catering menu, curated to help you imagine your table. Browse, then enquire." image={active?.image} />

      <section className="py-16 sm:py-24 bg-ivory">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          {/* category chips */}
          <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-4 mb-10">
            {menu.map((c) => (
              <button key={c.id} onClick={() => { setActive(c); setParams({ c: c.id }); }}
                data-testid={`menu-tab-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`shrink-0 px-6 py-3 rounded-full border text-sm tracking-wide transition-all ${active?.id === c.id ? "bg-burgundy text-ivory border-burgundy" : "border-gold/30 text-charcoal/70 hover:border-gold"}`}>
                {c.name}
              </button>
            ))}
          </div>

          {loading && <p className="text-center text-charcoal/50 py-20">Loading the collection…</p>}

          <AnimatePresence mode="wait">
            {active && (
              <motion.div key={active.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}>
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                  <div className="lg:col-span-4 lg:sticky lg:top-28">
                    <div className={`relative overflow-hidden border border-gold/30 shadow-2xl aspect-[3/4] ${shapeCls[active.shape] || "rounded-3xl"} group`}>
                      <img src={fileUrl(active.image)} alt={active.name} className="w-full h-full object-cover img-zoom" />
                      <div className="absolute inset-0 bg-gradient-to-t from-velvet/70 to-transparent" />
                    </div>
                    <h2 className="font-serif text-burgundy text-4xl mt-6">{active.name}</h2>
                    <p className="text-gold italic text-lg mt-1">{active.tagline}</p>
                    <button onClick={() => setEnquiry(active)} className="btn-burgundy mt-6 w-full justify-center" data-testid="menu-enquire">
                      Enquire About This Menu <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                    {(active.dishes || []).map((d, i) => (
                      <Reveal key={d.id} delay={(i % 2) * 0.06}>
                        <div className="group bg-cream border border-gold/15 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow flex">
                          <div className="w-28 sm:w-32 shrink-0 overflow-hidden">
                            <img src={fileUrl(d.image)} alt={d.name} className="w-full h-full object-cover img-zoom" />
                          </div>
                          <div className="p-4 sm:p-5 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-serif text-xl text-burgundy leading-tight">{d.name}</h3>
                              {d.price && <span className="text-gold text-sm font-medium shrink-0">{d.price}</span>}
                            </div>
                            <p className="text-charcoal/60 text-sm mt-1.5 leading-relaxed">{d.description}</p>
                            {(d.serving || d.min_qty) && <p className="text-[0.7rem] text-charcoal/40 mt-2 tracking-wide">{[d.serving, d.min_qty].filter(Boolean).join(" · ")}</p>}
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <section className="py-16 bg-cream text-center">
        <div className="max-w-2xl mx-auto px-4">
          <Overline className="justify-center">Build Your Own</Overline>
          <h2 className="font-serif text-burgundy text-4xl sm:text-5xl mt-4 mb-6">Design a menu around your event.</h2>
          <Link to="/quote-builder" className="btn-gold">Build Your Menu <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <Dialog open={!!enquiry} onOpenChange={(o) => !o && setEnquiry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-ivory">
          <DialogTitle className="sr-only">Enquire about {enquiry?.name}</DialogTitle>
          <h3 className="font-serif text-3xl text-burgundy mb-1">Enquire — {enquiry?.name}</h3>
          <p className="text-charcoal/60 text-sm mb-6">Tell us about your event and we'll craft a proposal around this menu.</p>
          <EnquiryForm preset={{ category: "MENU ENQUIRY", menu_ref: enquiry?.name, message: `Interested in the ${enquiry?.name} menu.` }} onDone={() => {}} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
